import type { EmailSecurity, EmailSettings, UpdateEmailSettingsInput } from './email-settings.api';

export interface EmailSettingsFormValues {
  enabled: boolean;
  host: string;
  port: string;
  security: EmailSecurity;
  username: string;
  password: string;
  clearPassword: boolean;
  fromName: string;
  fromAddress: string;
  replyTo: string;
}

export type EmailSettingsFormErrors = Partial<Record<keyof EmailSettingsFormValues, string>>;

// Mirrors `apps/api` `email-settings.dto.ts`.
const HOST_PATTERN = /^(?:[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?|[0-9A-Fa-f:]+)$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INVALID_EMAIL = 'Ingresá un correo válido.';

const SUGGESTED_PORTS: Record<EmailSecurity, number> = { NONE: 25, STARTTLS: 587, SSL_TLS: 465 };

export function suggestedPort(security: EmailSecurity): number {
  return SUGGESTED_PORTS[security];
}

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();

  return trimmed.length <= 254 && EMAIL_PATTERN.test(trimmed);
}

export const EMPTY_EMAIL_FORM: EmailSettingsFormValues = {
  enabled: false,
  host: '',
  port: String(SUGGESTED_PORTS.STARTTLS),
  security: 'STARTTLS',
  username: '',
  password: '',
  clearPassword: false,
  fromName: '',
  fromAddress: '',
  replyTo: '',
};

/** The password is never prefilled: it is write-only. */
export function formValuesFromSettings(settings: EmailSettings): EmailSettingsFormValues {
  if (!settings.configured) {
    return EMPTY_EMAIL_FORM;
  }

  return {
    enabled: settings.enabled,
    host: settings.host ?? '',
    port: String(settings.port ?? SUGGESTED_PORTS.STARTTLS),
    security: settings.security ?? 'STARTTLS',
    username: settings.username ?? '',
    password: '',
    clearPassword: false,
    fromName: settings.fromName ?? '',
    fromAddress: settings.fromAddress ?? '',
    replyTo: settings.replyTo ?? '',
  };
}

export function validateEmailSettingsForm(
  values: EmailSettingsFormValues,
  context: { hasStoredPassword: boolean },
): EmailSettingsFormErrors {
  const errors: EmailSettingsFormErrors = {};
  const host = values.host.trim();
  const port = Number(values.port);

  if (host === '' || host.length > 253 || !HOST_PATTERN.test(host)) {
    errors.host = 'Ingresá un servidor válido, sin protocolo ni puerto (por ejemplo smtp.empresa.com).';
  }

  if (values.port.trim() === '' || !Number.isInteger(port) || port < 1 || port > 65535) {
    errors.port = 'El puerto debe ser un número entero entre 1 y 65535.';
  }

  if (!isValidEmail(values.fromAddress)) {
    errors.fromAddress = INVALID_EMAIL;
  }

  if (values.replyTo.trim() !== '' && !isValidEmail(values.replyTo)) {
    errors.replyTo = INVALID_EMAIL;
  }

  const keepsStoredPassword = context.hasStoredPassword && !values.clearPassword;

  if (values.username.trim() !== '' && values.password === '' && !keepsStoredPassword) {
    errors.password = 'Ingresá la contraseña del usuario.';
  }

  return errors;
}

function nullIfBlank(value: string): string | null {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
}

/** Call only with values that passed `validateEmailSettingsForm`. */
export function toUpdateInput(values: EmailSettingsFormValues): UpdateEmailSettingsInput {
  const input: UpdateEmailSettingsInput = {
    enabled: values.enabled,
    host: values.host.trim(),
    port: Number(values.port),
    security: values.security,
    username: nullIfBlank(values.username),
    fromName: nullIfBlank(values.fromName),
    fromAddress: values.fromAddress.trim(),
    replyTo: nullIfBlank(values.replyTo),
  };

  if (values.clearPassword) {
    input.clearPassword = true;
  } else if (values.password !== '') {
    input.password = values.password;
  }

  return input;
}

/** True when saving would change what the SMTP server sees, so a previous test no longer vouches for it. */
export function connectionChanged(values: EmailSettingsFormValues, settings: EmailSettings): boolean {
  const saved = formValuesFromSettings(settings);

  return (
    values.password !== '' ||
    values.clearPassword ||
    values.host.trim() !== saved.host ||
    values.port.trim() !== saved.port ||
    values.security !== saved.security ||
    values.username.trim() !== saved.username ||
    values.fromName.trim() !== saved.fromName ||
    values.fromAddress.trim() !== saved.fromAddress ||
    values.replyTo.trim() !== saved.replyTo
  );
}
